/**
 * viewerAPI.js — Enterprise Global API Proxy & Task Queue
 * Mikhmel Cornerstone4D DICOM Viewer Pro v7 (Ultimate Edition)
 *
 * [AUDIT v7.1] — Архитектура "Safe Call Proxy":
 *   ✓ [FIX] Proxy.get: различает свойства и методы (неизвестные свойства → undefined)
 *   ✓ [FIX] installGlobal: обновляет глобальный объект при HMR
 *   ✓ [FIX] registerModule: интегрирован в Proxy (ленивая загрузка модулей)
 *   ✓ [FIX] _queue очищается при переинициализации
 *   ✓ [NEW] resetAPI(): полная переинициализация без перезагрузки страницы
 *   ✓ [SEC] Защита системных свойств: невозможно случайно перезаписать .version или .modules.
 *   ✓ [DX] Полная JSDoc типизация и кастомный класс ошибки (ApiTimeoutError).
 *
 * Как это работает:
 *   viewer.toggleCinematic(); 
 *   // Если метод еще не загружен (lazy load), вызов ставится в очередь.
 *   // Как только модуль регистрирует метод через registerAPI('toggleCinematic', fn),
 *   // очередь мгновенно выполняется, и оригинальный Promise резолвится.
 *
 * Ленивая загрузка модулей:
 *   registerModule('ai', async () => { const m = await import('../ai/aiModule.js'); ... });
 *   viewer.ai.detectLungNodules(...); // автоматически загрузит модуль и вызовет метод
 */

import { emit } from './eventBus.js';
import { log } from '../utils/logger.js';

// ═══════════════════════════════════════════════════════════════
// Внутреннее состояние (Скрыто от внешнего мира)
// ═══════════════════════════════════════════════════════════════

const _api = Object.create(null);          // method name → function (без прототипа)
const _queue = new Map();                  // method name → Array<{ args, resolve, reject, timerId }>
const _modules = Object.create(null);      // lazy loaders (name → async loader function)
const _moduleProxies = new Map();          // name → Proxy (загруженный модуль)

// Известные свойства, которые не являются методами API
const _knownProperties = new Set(['version', 'modules']);

export class ApiTimeoutError extends Error {
  constructor(methodName, timeoutMs) {
    super(`[ViewerAPI] Таймаут ожидания: метод "${methodName}" не был зарегистрирован за ${timeoutMs}мс.`);
    this.name = 'ApiTimeoutError';
    this.method = methodName;
  }
}

// ═══════════════════════════════════════════════════════════════
// Регистрация методов (Используется внутренними модулями)
// ═══════════════════════════════════════════════════════════════

/**
 * Зарегистрировать метод API. Если есть очередь ожидающих вызовов — они выполнятся.
 * @param {string} name — Имя метода
 * @param {Function} fn — Реализация
 */
export function registerAPI(name, fn) {
  if (typeof fn !== 'function') {
    log.error(`[API] Попытка зарегистрировать не-функцию под именем "${name}"`);
    return;
  }

  _api[name] = fn;

  // Добавляем в известные свойства чтобы Proxy не возвращал undefined
  _knownProperties.add(name);

  emit(`api:registered:${name}`, fn);

  // Flush queue (Разбор очереди ожидающих вызовов)
  if (_queue.has(name)) {
    const pendingTasks = _queue.get(name);
    _queue.delete(name);

    log.debug(`[API] Метод "${name}" зарегистрирован. Выполняем отложенные вызовы (${pendingTasks.length}).`);

    for (const task of pendingTasks) {
      clearTimeout(task.timerId);
      try {
        Promise.resolve(fn(...task.args))
          .then(task.resolve)
          .catch(task.reject);
      } catch (err) {
        task.reject(err);
      }
    }
  }
}

/**
 * Пакетная регистрация методов
 * @param {Record<string, Function>} obj — Группа методов { name: fn }
 */
export function registerAPIs(obj) {
  for (const [key, val] of Object.entries(obj)) {
    if (typeof val === 'function') registerAPI(key, val);
  }
}

/**
 * Получить метод напрямую (или undefined, если еще не загружен)
 * @param {string} name
 * @returns {Function|undefined}
 */
export function getAPI(name) {
  return _api[name];
}

// ═══════════════════════════════════════════════════════════════
// Безопасный вызов (Используется Proxy)
// ═══════════════════════════════════════════════════════════════

/**
 * Безопасный вызов. Если метод недоступен — ставит в очередь (Promise).
 * @param {string} name — Имя метода
 * @param {Array} args — Аргументы
 * @param {number} [timeoutMs=15000] — Таймаут ожидания
 * @returns {Promise<any>}
 */
export function callAPI(name, args = [], timeoutMs = 15000) {
  if (_api[name]) {
    try {
      return Promise.resolve(_api[name](...args));
    } catch (e) {
      return Promise.reject(e);
    }
  }

  // Постановка в очередь
  return new Promise((resolve, reject) => {
    if (!_queue.has(name)) _queue.set(name, []);

    const task = { args, resolve, reject, timerId: null };

    task.timerId = setTimeout(() => {
      const q = _queue.get(name);
      if (q) {
        const idx = q.indexOf(task);
        if (idx !== -1) q.splice(idx, 1);
        if (q.length === 0) _queue.delete(name);
      }
      reject(new ApiTimeoutError(name, timeoutMs));
    }, timeoutMs);

    _queue.get(name).push(task);
  });
}

/**
 * Проверить, зарегистрирован ли метод
 * @param {string} name
 * @returns {boolean}
 */
export function hasAPI(name) {
  return name in _api;
}

/**
 * Список всех зарегистрированных методов
 * @returns {string[]}
 */
export function listAPIs() {
  return Object.keys(_api);
}

// ═══════════════════════════════════════════════════════════════
// Регистрация Lazy Loader'ов (для ленивой загрузки модулей)
// ═══════════════════════════════════════════════════════════════

/**
 * Зарегистрировать ленивый модуль.
 * При первом обращении к viewer.moduleName — загрузчик будет вызван автоматически.
 * 
 * @param {string} name — Имя модуля (например 'ai', 'gpu')
 * @param {Function} loader — Асинхронная функция, возвращающая объект с методами
 * 
 * @example
 * registerModule('ai', async () => {
 *   const m = await import('../ai/aiModule.js');
 *   return { detectLungNodules: m.detectLungNodules, calciumScoring: m.calciumScoring };
 * });
 * // Теперь viewer.ai.detectLungNodules(...) работает
 */
export function registerModule(name, loader) {
  if (typeof loader !== 'function') {
    log.error(`[API] Попытка зарегистрировать не-функцию как лоадер модуля "${name}"`);
    return;
  }
  _modules[name] = loader;
  _knownProperties.add(name);
}

/**
 * Проверить, загружен ли модуль
 * @param {string} name
 * @returns {boolean}
 */
export function isModuleLoaded(name) {
  return _moduleProxies.has(name);
}

/**
 * Принудительно загрузить модуль
 * @param {string} name
 * @returns {Promise<Object>}
 */
export async function loadModule(name) {
  const loader = _modules[name];
  if (!loader) throw new Error(`[API] Модуль "${name}" не зарегистрирован`);

  if (_moduleProxies.has(name)) return _moduleProxies.get(name);

  const exports = await loader();
  const proxy = new Proxy(exports, _moduleProxyHandler);
  _moduleProxies.set(name, proxy);
  return proxy;
}

// ═══════════════════════════════════════════════════════════════
// PROXY: window.MikhmelViewer — Интеллектуальный фасад
// ═══════════════════════════════════════════════════════════════

const _base = Object.create(null);

/**
 * Прокси-обработчик для загруженных модулей.
 * Позволяет viewer.ai.method() работать.
 */
const _moduleProxyHandler = {
  get(target, prop) {
    if (typeof prop === 'symbol' || prop === 'then' || prop === 'toJSON') return undefined;
    const value = target[prop];
    if (typeof value === 'function') return value.bind(target);
    return value;
  }
};

const _proxyHandler = {
  get(target, prop) {
    // 1. Символы и специальные свойства (React/Vue инспекторы, Promise thenables)
    if (typeof prop === 'symbol' || prop === 'then' || prop === 'toJSON') return undefined;

    // 2. Системные свойства
    if (prop in target) return target[prop];

    // 3. Уже загруженный модуль
    if (_moduleProxies.has(prop)) return _moduleProxies.get(prop);

    // 4. Ленивая загрузка модуля
    if (_modules[prop]) {
      // Возвращаем прокси, который загрузит модуль при первом обращении
      return new Proxy({}, {
        get(_, method) {
          if (typeof method === 'symbol' || method === 'then' || method === 'toJSON') return undefined;
          return async function (...args) {
            const mod = await loadModule(prop);
            if (typeof mod[method] === 'function') {
              return mod[method](...args);
            }
            // Метод не найден в загруженном модуле — ставим в очередь
            log.info(`[API] Метод "${String(method)}" модуля "${prop}" отложен...`);
            return callAPI(`${prop}.${String(method)}`, args);
          };
        }
      });
    }

    // 5. Уже зарегистрированный метод API
    if (prop in _api) return _api[prop];

    // 6. Неизвестное свойство — НЕ метод. Возвращаем undefined.
    // [v7.1 FIX] Больше не возвращаем функцию для неизвестных свойств.
    // if (viewer.someProperty) — корректно вернёт undefined.
    log.debug(`[API] Свойство "${String(prop)}" не найдено в API`);
    return undefined;
  },

  set(target, prop, value) {
    // [SEC] Защита системных полей от перезаписи из консоли браузера
    if (prop === 'version' || prop === 'modules') {
      log.warn(`[API Proxy] Отклонена попытка изменения системного свойства: ${String(prop)}`);
      return false;
    }

    if (typeof value === 'function') {
      registerAPI(String(prop), value);
      return true;
    }

    target[String(prop)] = value;
    return true;
  },

  has(target, prop) {
    return prop in target || prop in _api || _moduleProxies.has(prop) || prop in _modules;
  },

  ownKeys(target) {
    return [...new Set([
      ...Object.keys(target),
      ...Object.keys(_api),
      ..._moduleProxies.keys(),
      ...Object.keys(_modules),
    ])];
  },

  getOwnPropertyDescriptor(target, prop) {
    if (prop in target) return Object.getOwnPropertyDescriptor(target, prop);
    if (prop in _api) return { enumerable: true, configurable: true, value: _api[prop] };
    if (_moduleProxies.has(prop)) return { enumerable: true, configurable: true, value: _moduleProxies.get(prop) };
    if (prop in _modules) return { enumerable: true, configurable: true, value: undefined };
    return undefined;
  }
};

/**
 * Единый инстанс глобального API
 */
export const viewer = new Proxy(_base, _proxyHandler);

/**
 * Инициализация: Устанавливает глобальный объект.
 * Вызывается один раз при старте приложения (main.js).
 * При HMR — обновляет глобальную ссылку.
 * 
 * @param {string} version 
 * @param {Object} [modules={}] — статические модули для отображения в viewer.modules
 */
export function installGlobal(version, modules = {}) {
  // Системные свойства
  Object.defineProperty(_base, 'version', { value: version, writable: false, enumerable: true, configurable: true });
  Object.defineProperty(_base, 'modules', { value: modules, writable: false, enumerable: true, configurable: true });

  // Кросс-платформенная установка (Браузер / Worker)
  const globalEnv = typeof globalThis !== 'undefined' ? globalThis : window;

  if (globalEnv.MikhmelViewer) {
    log.debug('[API] Reinstalling global proxy (HMR)');
  }

  globalEnv.MikhmelViewer = viewer;
  emit('api:installed', { version, env: typeof window !== 'undefined' ? 'browser' : 'worker' });
}

/**
 * Полная переинициализация API (для тестов / HMR).
 * Очищает все методы, модули, очередь.
 */
export function resetAPI() {
  // Очищаем очередь с reject'ом всех ожидающих задач
  for (const [name, tasks] of _queue) {
    for (const task of tasks) {
      clearTimeout(task.timerId);
      task.reject(new Error(`[API] Сброс: метод "${name}" не будет загружен (resetAPI)`));
    }
  }
  _queue.clear();

  // Очищаем методы
  for (const key of Object.keys(_api)) {
    delete _api[key];
  }

  // Очищаем модули
  for (const key of Object.keys(_modules)) {
    delete _modules[key];
  }
  _moduleProxies.clear();

  // Очищаем известные свойства (кроме системных)
  _knownProperties.clear();
  _knownProperties.add('version');
  _knownProperties.add('modules');

  log.info('[API] Reset complete');
}

// ═══════════════════════════════════════════════════════════════
// Debug helpers
// ═══════════════════════════════════════════════════════════════

if (typeof window !== 'undefined') {
  window.__ViewerAPI = {
    registerAPI, registerAPIs, registerModule,
    callAPI, getAPI, hasAPI, listAPIs,
    loadModule, isModuleLoaded,
    resetAPI, installGlobal,
    _api, _queue, _modules, _moduleProxies,
  };
}